import React, { useState, useEffect, useMemo } from 'react';
import { fetchConversationDetails } from '../services/dataAnalyticsApi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import '../styles/UsageStats.css';
import { 
  Calendar, 
  Users, 
  MessageSquare, 
  ThumbsUp, 
  ThumbsDown, 
  Activity,
  TrendingUp,
  Clock,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Download,
  BarChart3,
  UserCheck,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const UsageStats = () => {
  const [allData, setAllData] = useState([]); // Store all fetched data
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  const recordsPerPage = 25;

  // Fetch data when dates change
  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);
  
  // Update displayed page when page changes or data changes
  useEffect(() => {
    if (allData.length > 0) {
      const startIndex = (currentPage - 1) * recordsPerPage;
      const endIndex = startIndex + recordsPerPage;
      const pageData = allData.slice(startIndex, endIndex);
      setConversations(pageData);
    }
  }, [currentPage, allData]);

  useEffect(() => {
    filterConversations();
  }, [conversations, searchQuery]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setCurrentPage(1); // Reset to first page when fetching new data
      
      // Fetch ALL data without limit to get total count and all records
      const response = await fetchConversationDetails({
        start_date: startDate || undefined,
        end_date: endDate || undefined
      });
      
      if (response.data && response.data.status === 'success') {
        const fetchedData = response.data.data || [];
        
        // Store all data
        setAllData(fetchedData);
        
        // Set total records from actual data length
        setTotalRecords(fetchedData.length);
        
        // Set initial page data
        const pageData = fetchedData.slice(0, recordsPerPage);
        setConversations(pageData);
        
        setError(null);
      } else {
        throw new Error('Failed to fetch conversation details');
      }
    } catch (err) {
      console.error('Error fetching usage stats:', err);
      setError('Failed to load usage statistics. Please try again later.');
      setAllData([]);
      setConversations([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const filterConversations = () => {
    if (!searchQuery) {
      setFilteredConversations(conversations);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = conversations.filter(conv => {
      const userEmail = conv.user?.email?.toLowerCase() || '';
      const userName = `${conv.user?.first_name || ''} ${conv.user?.last_name || ''}`.toLowerCase();
      const question = conv.chat?.question?.toLowerCase() || '';
      const answer = conv.chat?.answer?.toLowerCase() || '';
      const domain = conv.user?.domain?.toLowerCase() || '';

      return userEmail.includes(query) ||
             userName.includes(query) ||
             question.includes(query) ||
             answer.includes(query) ||
             domain.includes(query);
    });

    setFilteredConversations(filtered);
  };

  const handleDateChange = (e, type) => {
    const value = e.target.value;
    if (type === 'start') {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
    setCurrentPage(1);
  };

  const toggleRowExpanded = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Email', 'Name', 'Domain', 'Question', 'Answer', 'Asked At', 'Status', 'Liked', 'Disliked'],
      ...filteredConversations.map(conv => [
        conv.user?.email || '',
        `${conv.user?.first_name || ''} ${conv.user?.last_name || ''}`.trim(),
        conv.user?.domain || '',
        conv.chat?.question || '',
        conv.chat?.answer || '',
        conv.chat?.questioned_at || '',
        conv.chat?.status || '',
        conv.chat?.feedback?.is_liked ? 'Yes' : 'No',
        conv.chat?.feedback?.is_disliked ? 'Yes' : 'No'
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `usage-stats-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Calculate total pages based on total records from API
  const totalPages = useMemo(() => {
    return Math.ceil(totalRecords / recordsPerPage) || 1;
  }, [totalRecords, recordsPerPage]);

  // Memoize calculated values to prevent recalculation on every render
  const stats = useMemo(() => {
    const uniqueUsers = new Set(conversations.map(c => c.user?.email)).size;
    const positiveFeedback = conversations.filter(c => c.chat?.feedback?.is_liked).length;
    const negativeFeedback = conversations.filter(c => c.chat?.feedback?.is_disliked).length;
    const feedbackRate = conversations.length > 0 
      ? Math.round(((positiveFeedback + negativeFeedback) / conversations.length) * 100)
      : 0;
    const successfulChats = conversations.filter(c => c.chat?.status === 'completed').length;
    const successRate = conversations.length > 0 
      ? Math.round((successfulChats / conversations.length) * 100)
      : 0;
    
    return {
      uniqueUsers,
      positiveFeedback,
      negativeFeedback,
      feedbackRate,
      successfulChats,
      successRate
    };
  }, [conversations]);

  // Custom components for ReactMarkdown
  const markdownComponents = {
    code({node, inline, className, children, ...props}) {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={match[1]}
          PreTag="div"
          className="code-block-syntax"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className="inline-code" {...props}>
          {children}
        </code>
      );
    },
    p: ({children}) => <p className="md-paragraph">{children}</p>,
    ul: ({children}) => <ul className="md-list">{children}</ul>,
    ol: ({children}) => <ol className="md-list md-list-ordered">{children}</ol>,
    li: ({children}) => <li className="md-list-item">{children}</li>,
    h1: ({children}) => <h1 className="md-heading md-h1">{children}</h1>,
    h2: ({children}) => <h2 className="md-heading md-h2">{children}</h2>,
    h3: ({children}) => <h3 className="md-heading md-h3">{children}</h3>,
    h4: ({children}) => <h4 className="md-heading md-h4">{children}</h4>,
    blockquote: ({children}) => <blockquote className="md-blockquote">{children}</blockquote>,
    a: ({children, href}) => <a href={href} className="md-link" target="_blank" rel="noopener noreferrer">{children}</a>,
    table: ({children}) => <table className="md-table">{children}</table>,
    thead: ({children}) => <thead className="md-table-head">{children}</thead>,
    tbody: ({children}) => <tbody className="md-table-body">{children}</tbody>,
    tr: ({children}) => <tr className="md-table-row">{children}</tr>,
    th: ({children}) => <th className="md-table-header">{children}</th>,
    td: ({children}) => <td className="md-table-cell">{children}</td>,
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div className="usage-stats-container">
      {/* Header Section */}
      <div className="usage-header">
        <div className="header-content">
          <div>
            <h1 className="header-title">Usage Analytics</h1>
            <p className="header-subtitle">Monitor conversation metrics and user interactions</p>
          </div>
          <button className="export-button" onClick={exportToCSV}>
            <Download size={16} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div>
            <div className="metric-header">
              <div className="metric-icon-wrapper blue">
                <MessageSquare size={18} />
              </div>
              <span className="metric-label">Total Conversations</span>
            </div>
            <div className="metric-value">{totalRecords.toLocaleString()}</div>
          </div>
          <div className="metric-footer">
            <TrendingUp size={12} />
            <span>All time</span>
          </div>
        </div>

        <div className="metric-card">
          <div>
            <div className="metric-header">
              <div className="metric-icon-wrapper purple">
                <Users size={18} />
              </div>
              <span className="metric-label">Unique Users</span>
            </div>
            <div className="metric-value">{stats.uniqueUsers.toLocaleString()}</div>
          </div>
          <div className="metric-footer">
            <UserCheck size={12} />
            <span>Active users</span>
          </div>
        </div>

        <div className="metric-card">
          <div>
            <div className="metric-header">
              <div className="metric-icon-wrapper green">
                <Activity size={18} />
              </div>
              <span className="metric-label">Success Rate</span>
            </div>
            <div className="metric-value">{stats.successRate}%</div>
          </div>
          <div className="metric-footer">
            <BarChart3 size={12} />
            <span>Completed chats</span>
          </div>
        </div>

        <div className="metric-card">
          <div>
            <div className="metric-header">
              <div className="metric-icon-wrapper amber">
                <ThumbsUp size={18} />
              </div>
              <span className="metric-label">Feedback Rate</span>
            </div>
            <div className="metric-value">{stats.feedbackRate}%</div>
          </div>
          <div className="metric-footer">
            <div className="feedback-stats">
              <span className="feedback-stat positive">👍 {stats.positiveFeedback}</span>
              <span className="feedback-stat negative">👎 {stats.negativeFeedback}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="search-wrapper">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search conversations..."
              className="search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="date-filters">
            <div className="date-input-wrapper">
              <Calendar size={16} />
              <input
                type="date"
                className="date-input"
                value={startDate}
                onChange={(e) => handleDateChange(e, 'start')}
                placeholder="Start date"
              />
            </div>
            <span className="date-separator">to</span>
            <div className="date-input-wrapper">
              <Calendar size={16} />
              <input
                type="date"
                className="date-input"
                value={endDate}
                onChange={(e) => handleDateChange(e, 'end')}
                placeholder="End date"
              />
            </div>
          </div>

          {(startDate || endDate || searchQuery) && (
            <button 
              className="clear-filters-btn"
              onClick={() => {
                setStartDate('');
                setEndDate('');
                setSearchQuery('');
                setCurrentPage(1);
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Conversations Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading conversations...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <p>{error}</p>
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="empty-state">
            <MessageSquare size={48} />
            <h3>No conversations found</h3>
            <p>Try adjusting your filters or date range</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="conversations-table">
              <thead>
                <tr>
                  <th style={{width: '25%'}}>User</th>
                  <th style={{width: '45%'}}>Conversation</th>
                  <th style={{width: '15%'}}>Time</th>
                  <th style={{width: '8%'}}>Status</th>
                  <th style={{width: '7%'}}>Feedback</th>
                </tr>
              </thead>
              <tbody>
                {filteredConversations.map((conv, idx) => {
                  const rowId = `${conv.session?.conversation_id}-${conv.chat?.query_id}-${idx}`;
                  const isExpanded = expandedRows.has(rowId);
                  
                  return (
                    <tr key={rowId} className="conversation-row">
                      <td className="user-cell">
                        <div className="user-info">
                          <div className="user-avatar">
                            {(conv.user?.first_name?.[0] || conv.user?.email?.[0] || 'U').toUpperCase()}
                          </div>
                          <div className="user-details">
                            <div className="user-name">
                              {conv.user?.first_name && conv.user?.last_name
                                ? `${conv.user.first_name} ${conv.user.last_name}`
                                : conv.user?.email?.split('@')[0] || 'Unknown User'}
                            </div>
                            <div className="user-email">{conv.user?.email || '-'}</div>
                            <div className="user-domain">{conv.user?.domain || '-'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="conversation-cell">
                        <div className="conversation-wrapper">
                          <div 
                            className="expand-toggle"
                            onClick={() => toggleRowExpanded(rowId)}
                          >
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </div>
                          <div className="qa-content">
                            <div className="question-row">
                              <span className="qa-label">Q:</span>
                              <span className="question-text">{conv.chat?.question || '-'}</span>
                              {/* Show indicators for available metadata - clickable to expand */}
                              {!isExpanded && (
                                <div 
                                  className="metadata-indicators"
                                  onClick={() => toggleRowExpanded(rowId)}
                                  style={{ cursor: 'pointer' }}
                                  title="Click to expand and see details"
                                >
                                  {conv.chat?.execution_time && (
                                    <span className="indicator execution-time">
                                      ⏱️ {typeof conv.chat.execution_time === 'number' 
                                        ? `${conv.chat.execution_time.toFixed(2)}s`
                                        : conv.chat.execution_time}
                                    </span>
                                  )}
                                  {conv.chat?.questions_generated && (
                                    <span className="indicator questions">❓ Questions</span>
                                  )}
                                  {conv.chat?.query_list && (
                                    <span className="indicator queries">📝 Queries</span>
                                  )}
                                </div>
                              )}
                            </div>
                            {isExpanded && (
                              <>
                                {/* Always show questions_generated and execution_time at the top when expanded */}
                                {conv.chat?.execution_time && (
                                  <div className="metadata-row">
                                    <span className="metadata-label">Execution Time:</span>
                                    <div className="metadata-content">
                                      <span className="execution-time-badge">
                                        ⏱️ {typeof conv.chat.execution_time === 'number' 
                                          ? `${conv.chat.execution_time.toFixed(3)}s`
                                          : conv.chat.execution_time}
                                      </span>
                                    </div>
                                  </div>
                                )}
                                
                                {conv.chat?.answer && (
                                  <div className="answer-row">
                                    <span className="qa-label">A:</span>
                                    <div className="answer-content">
                                      <ReactMarkdown 
                                        remarkPlugins={[remarkGfm]}
                                        components={markdownComponents}
                                      >
                                        {conv.chat.answer}
                                      </ReactMarkdown>
                                    </div>
                                  </div>
                                )}
                                
                                {conv.chat?.questions_generated && (
                                  <div className="metadata-row">
                                    <span className="metadata-label">Generated Questions:</span>
                                    <div className="metadata-content">
                                      {(() => {
                                        let questions = conv.chat.questions_generated;
                                        
                                        // Parse if it's a JSON string
                                        if (typeof questions === 'string') {
                                          try {
                                            questions = JSON.parse(questions);
                                          } catch (e) {
                                            // If not JSON, split by newlines or display as is
                                            const lines = questions.split('\n').filter(line => line.trim());
                                            if (lines.length > 1) {
                                              return (
                                                <div className="code-list-container">
                                                  {lines.map((line, idx) => (
                                                    <div key={idx} className="code-list-item">
                                                      <span className="code-list-number">{idx + 1}</span>
                                                      <code className="code-list-content">{line.trim()}</code>
                                                    </div>
                                                  ))}
                                                </div>
                                              );
                                            }
                                            return <code className="inline-metadata-code">{questions}</code>;
                                          }
                                        }
                                        
                                        if (Array.isArray(questions)) {
                                          return (
                                            <div className="code-list-container">
                                              {questions.map((q, idx) => (
                                                <div key={idx} className="code-list-item">
                                                  <span className="code-list-number">{idx + 1}</span>
                                                  <code className="code-list-content">
                                                    {typeof q === 'object' ? JSON.stringify(q, null, 2) : q}
                                                  </code>
                                                </div>
                                              ))}
                                            </div>
                                          );
                                        }
                                        
                                        return <code className="inline-metadata-code">{JSON.stringify(questions, null, 2)}</code>;
                                      })()}
                                    </div>
                                  </div>
                                )}
                                
                                {conv.chat?.query_list && (
                                  <div className="metadata-row">
                                    <span className="metadata-label">Query List:</span>
                                    <div className="metadata-content">
                                      {(() => {
                                        let queries = conv.chat.query_list;
                                        
                                        // Parse if it's a JSON string
                                        if (typeof queries === 'string') {
                                          try {
                                            queries = JSON.parse(queries);
                                          } catch (e) {
                                            // If not JSON, split by common delimiters or display as is
                                            const lines = queries.split(/[\n;]/).filter(line => line.trim());
                                            if (lines.length > 1) {
                                              return (
                                                <div className="code-list-container">
                                                  {lines.map((line, idx) => (
                                                    <div key={idx} className="code-list-item">
                                                      <span className="code-list-number">{idx + 1}</span>
                                                      <pre className="code-list-query">{line.trim()}</pre>
                                                    </div>
                                                  ))}
                                                </div>
                                              );
                                            }
                                            return <code className="inline-metadata-code">{queries}</code>;
                                          }
                                        }
                                        
                                        if (Array.isArray(queries)) {
                                          return (
                                            <div className="code-list-container">
                                              {queries.map((query, idx) => (
                                                <div key={idx} className="code-list-item">
                                                  <span className="code-list-number">{idx + 1}</span>
                                                  <pre className="code-list-query">
                                                    {typeof query === 'object' 
                                                      ? JSON.stringify(query, null, 2) 
                                                      : query
                                                    }
                                                  </pre>
                                                </div>
                                              ))}
                                            </div>
                                          );
                                        } else if (typeof queries === 'object') {
                                          return (
                                            <pre className="json-display-formatted">
                                              {JSON.stringify(queries, null, 2)}
                                            </pre>
                                          );
                                        }
                                        
                                        return <code className="inline-metadata-code">{queries}</code>;
                                      })()}
                                    </div>
                                  </div>
                                )}
                                
                                {conv.chat?.artifact_id && (
                                  <div className="metadata-row">
                                    <span className="metadata-label">Artifact ID:</span>
                                    <div className="metadata-content">
                                      <code className="artifact-id">{conv.chat.artifact_id}</code>
                                    </div>
                                  </div>
                                )}
                                
                                {conv.chat?.execution_time && (
                                  <div className="metadata-row">
                                    <span className="metadata-label">Execution Time:</span>
                                    <div className="metadata-content">
                                      <span className="execution-time">
                                        {typeof conv.chat.execution_time === 'number' 
                                          ? `${conv.chat.execution_time.toFixed(2)}s`
                                          : conv.chat.execution_time}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="time-cell">
                        <div className="time-info">
                          <Clock size={14} />
                          <span>{formatDate(conv.chat?.questioned_at)}</span>
                        </div>
                      </td>
                      <td className="status-cell">
                        <span className={`status-badge status-${conv.chat?.status || 'unknown'}`}>
                          {conv.chat?.status || 'unknown'}
                        </span>
                      </td>
                      <td className="feedback-cell">
                        {conv.chat?.feedback?.is_liked ? (
                          <span className="feedback-badge positive">
                            <ThumbsUp size={14} />
                          </span>
                        ) : conv.chat?.feedback?.is_disliked ? (
                          <span className="feedback-badge negative">
                            <ThumbsDown size={14} />
                          </span>
                        ) : (
                          <span className="feedback-badge neutral">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-info">
            Showing {conversations.length > 0 ? ((currentPage - 1) * recordsPerPage) + 1 : 0} to{' '}
            {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords} conversations
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              title="First page"
            >
              First
            </button>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} />
            </button>
            
            <div className="page-numbers">
              {getPageNumbers().map((pageNum, idx) => (
                pageNum === '...' ? (
                  <span key={`ellipsis-${idx}`} className="page-ellipsis">...</span>
                ) : (
                  <button
                    key={pageNum}
                    className={`page-number ${pageNum === currentPage ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                )
              ))}
            </div>
            
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight size={16} />
            </button>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              title="Last page"
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsageStats;